import React, { useEffect, useRef, useState, memo } from "react";
import { FaBars } from "react-icons/fa";
import useTheme from "../../hooks/useTheme";
import { useTranslation } from 'react-i18next';

const AutoScrollCategories = memo(({
  categories,
  categoriesStatus,
  selectedCategory,
  onCategorySelect,
  onFilterClick,
  isSticky = false,
}) => {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const theme = useTheme();
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile vs desktop
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Refs for drag scroll
  const categoryBarRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  // Auto-scroll
  const isAutoScrolling = true;
  const isPausedRef = useRef(false);
  const lastScrollPosition = useRef(0);

  useEffect(() => {
    if (!isAutoScrolling || !categoryBarRef.current || !categories) return;

    let scrollPosition;
    const scrollSpeed = 0.35; // Pixels per frame for smooth scrolling
    let animationFrameId;

    const autoScroll = () => {
      const scrollContainer = categoryBarRef.current;
      const scrollContent = scrollContainer?.querySelector(".category-division");

      if (!scrollContainer || !scrollContent || isPausedRef.current || isDragging.current) {
        animationFrameId = requestAnimationFrame(autoScroll);
        return;
      }

      const scrollWidth = scrollContent.scrollWidth;
      const containerWidth = scrollContainer.clientWidth;

      // If content fits or dimensions are not available yet, just idle but keep loop alive
      if (scrollWidth <= containerWidth || containerWidth === 0) {
        animationFrameId = requestAnimationFrame(autoScroll);
        return;
      }

      const halfScrollWidth = scrollWidth / 2;
      const isArabic = i18n.language === 'ar';
      
      // Initialize scrollPosition from ref if it's the first run or was reset
      if (typeof scrollPosition === 'undefined') {
        scrollPosition = lastScrollPosition.current;
      }
      
      scrollPosition += scrollSpeed;

      // When we reach the end of the first set, seamlessly reset to the beginning
      if (scrollPosition >= halfScrollWidth) {
        scrollPosition = 0;
      }

      const isRTL = isArabic; // simplified check
      
      // Handle RTL scroll directionality across browsers
      if (isRTL) {
        scrollContainer.scrollLeft = -scrollPosition;
        
        // Safari and some older browsers might use positive values
        if (scrollContainer.scrollLeft === 0 && scrollPosition > 0) {
          scrollContainer.scrollLeft = scrollPosition;
        }
      } else {
        scrollContainer.scrollLeft = scrollPosition;
      }
      
      lastScrollPosition.current = scrollPosition;
      animationFrameId = requestAnimationFrame(autoScroll);
    };

    animationFrameId = requestAnimationFrame(autoScroll);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isAutoScrolling, categories, isMobile, i18n.language]);


  // Drag-to-scroll
  const handleMouseDown = (e) => {
    isDragging.current = true;
    isPausedRef.current = true;
    startX.current = e.pageX - categoryBarRef.current.offsetLeft;
    scrollLeft.current = categoryBarRef.current.scrollLeft;
  };

  const handleMouseLeave = () => {
    isDragging.current = false;
    isPausedRef.current = false;
    // Save the current scroll position when mouse leaves
    if (categoryBarRef.current) {
      lastScrollPosition.current = Math.abs(categoryBarRef.current.scrollLeft);
    }
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    // We don't set isPausedRef.current = false here because 
    // the user might still be hovering over the bar.
    // handleMouseLeave will handle resuming the scroll.
    if (categoryBarRef.current) {
      lastScrollPosition.current = Math.abs(categoryBarRef.current.scrollLeft);
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - categoryBarRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    categoryBarRef.current.scrollLeft = scrollLeft.current - walk;
  };

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    isDragging.current = true;
    isPausedRef.current = true;
    startX.current = e.touches[0].pageX - categoryBarRef.current.offsetLeft;
    scrollLeft.current = categoryBarRef.current.scrollLeft;
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.touches[0].pageX - categoryBarRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    categoryBarRef.current.scrollLeft = scrollLeft.current - walk;
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
    isPausedRef.current = false;
    // Save the current scroll position when user stops touching
    if (categoryBarRef.current) {
      lastScrollPosition.current = Math.abs(categoryBarRef.current.scrollLeft);
    }
  };

  // Add touch event listeners with passive: false to allow preventDefault
  useEffect(() => {
    const categoryBar = categoryBarRef.current;
    if (!categoryBar) return;

    const touchStartHandler = (e) => handleTouchStart(e);
    const touchMoveHandler = (e) => handleTouchMove(e);
    const touchEndHandler = () => handleTouchEnd();

    // Add event listeners with passive: false
    categoryBar.addEventListener("touchstart", touchStartHandler, {
      passive: false,
    });
    categoryBar.addEventListener("touchmove", touchMoveHandler, {
      passive: false,
    });
    categoryBar.addEventListener("touchend", touchEndHandler, {
      passive: false,
    });

    return () => {
      categoryBar.removeEventListener("touchstart", touchStartHandler);
      categoryBar.removeEventListener("touchmove", touchMoveHandler);
      categoryBar.removeEventListener("touchend", touchEndHandler);
    };
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "row" }}>
      <div className="flex-shrink-0 w-12 md:w-16 flex items-center justify-center bg-white z-10 border-b border-gray-200 bg-white">
        {categoriesStatus !== "loading" && (
          <div
            className="cursor-pointer hover:scale-110 transition-transform"
            onClick={onFilterClick}
            title="View All Categories"
          >
            <div
              className="w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-shadow"
              style={{
                border: `1px solid ${theme.colors.allCategoriesBorder}`,
                backgroundColor: theme.colors.allCategoriesBg || "#ffffff",
              }}
            >
               <FaBars
                 className="w-4 h-4 md:w-5 md:h-5"
                 style={{ color: theme.colors.menuIconColor || theme.colors.primaryColor }}
               />
            </div>
          </div>
        )}
      </div>

      <div
        ref={categoryBarRef}
        className="border-b border-gray-200 bg-white"
        style={{
          WebkitOverflowScrolling: "touch",
          cursor: isDragging.current ? "grabbing" : "grab",
          width: "100%",
          overflow: "hidden",
          willChange: "scroll-position",
          transform: "translateZ(0)",
        }}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={(e) => {
          handleMouseMove(e);
          isPausedRef.current = true;
        }}
        onMouseEnter={() => { isPausedRef.current = true; }}
      >
        <div className="flex items-center">
          {/* Fixed Filter Icon - Not Scrollable */}

          {/* Scrollable Categories Container */}
          <div
            className={`flex items-center space-x-2 md:space-x-6 ${isArabic ? 'space-x-reverse' : ''} category-division overflow-x-auto whitespace-nowrap py-3 md:py-4 px-4 md:px-6 scrollbar-hide`}
            style={{
              minWidth: "max-content",
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              touchAction: "pan-x",
              overflowX: "auto",
              width: "max-content",
              paddingLeft: "16px",
              paddingRight: "16px",
              willChange: "transform",
              transform: "translateZ(0)",
              backfaceVisibility: "hidden",
              perspective: "1000px",
            }}
          >
            {categoriesStatus === "loading"
              ? Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="animate-pulse flex-shrink-0">
                    <div className="w-20 md:w-24 h-8 md:h-10 bg-gray-200 rounded-full"></div>
                  </div>
                ))
              : categories && (() => {
                  // Mobile: duplicate if more than 3 categories (they don't fit, so duplicates won't show)
                  // Desktop: duplicate only if more than 9 categories (prevents visible duplicates when 4 items fit)
                  const minCategoriesForDuplicate = isMobile ? 3 : 9;
                  const shouldDuplicate = categories.length > minCategoriesForDuplicate;
                  const categoriesToRender = shouldDuplicate ? [...categories, ...categories] : categories;
                  
                  return categoriesToRender.map((categoryItem, index) => (
                  <div
                    key={`${categoryItem.id || categoryItem.name}-${index}`}
                    className={`flex-shrink-0 ${
                      selectedCategory === categoryItem.name
                        ? "active_menu_tab poppins bg-primary/10 text-primary px-2 md:px-3 py-1 md:py-2 font-medium text-xs flex flex-col items-center rounded-lg"
                        : "menu_tab poppins text-gray-600 px-2 md:px-3 py-1 md:py-2 font-medium text-xs hover:text-primary flex flex-col items-center"
                    }`}
                    onClick={() => onCategorySelect(categoryItem.name, index % categories.length)}
                  >
                    {categoryItem.DocImage?.imageUrl ? (
                      <img
                        src={categoryItem.DocImage.imageUrl}
                        alt={categoryItem.name}
                        className={`w-8 h-8 md:w-12 md:h-12 object-contain rounded-full mb-1 md:mb-2 p-1`}
                        style={{
                          border: `1px solid ${
                            selectedCategory === categoryItem.name
                              ? theme.colors.categoryImageBorder
                              : "#d1d5db"
                          }`,
                          backgroundColor:
                            selectedCategory === categoryItem.name
                              ? theme.colors.categoryImageBorder
                              : "",
                        }}
                      />
                    ) : (
                      <div
                        className={`w-8 h-8 md:w-12 md:h-12 rounded-full mb-1 md:mb-2 p-1 flex items-center justify-center`}
                        style={{
                          border: `1px solid ${
                            selectedCategory === categoryItem.name
                              ? theme.colors.categoryImageBorder
                              : "#d1d5db"
                          }`,
                          backgroundColor:
                            selectedCategory === categoryItem.name
                              ? theme.colors.categoryImageBorder
                              : "",
                        }}
                      >
                        <svg
                          width="24"
                          height="24"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                          className="md:w-8 md:h-8"
                        >
                          <circle
                            cx="8"
                            cy="8"
                            r="7"
                            stroke={theme.colors.categoryImageBorder}
                            strokeWidth="2"
                            fill="#fff"
                          />
                          <text
                            x="8"
                            y="12"
                            textAnchor="middle"
                            fontSize="10"
                            fill={theme.colors.categoryImageBorder}
                          >
                            🍽️
                          </text>
                        </svg>
                      </div>
                    )}
                    <span
                      className={`px-2 md:px-4 py-1 md:py-2 rounded-lg text-xs md:text-sm`}
                      style={{
                        backgroundColor:
                          selectedCategory === categoryItem.name
                            ? theme.colors.categorySelectedTextBg || theme.colors.primaryColor
                            : theme.colors.allCategoriesBg || "transparent",
                        color:
                          selectedCategory === categoryItem.name
                            ? theme.colors.categorySelectedText || theme.colors.primaryColor
                            : theme.name === 'palm' ? '#000000' : (theme.colors.allCategoriesText || theme.colors.textPrimary || '#000000'),
                        border: selectedCategory === categoryItem.name
                          ? `1px solid ${theme.colors.categoryImageBorder || theme.colors.primaryColor}`
                          : `1px solid ${theme.colors.allCategoriesBorder || '#d1d5db'}`,
                      }}
                    >
                      {(isArabic && categoryItem.name_ar) ? categoryItem.name_ar : categoryItem.name}
                    </span>
                  </div>
                  ));
                })()}
          </div>
        </div>
      </div>
    </div>
  );
});

export default AutoScrollCategories;
